import { FunctionComponent, ReactNode } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2 } from "@mui/material";
import { useTranslation } from "react-i18next";

type Props = {
  dialogTitle?: string;
  dialogDesc: string;
  isDeleteModalOpen: boolean;
  setIsDeleteModalOpen: (o: boolean) => void;
  onConfirm: () => void;
  deleteStatus: "error" | "loading" | "success" | "idle";
  /** Optional content between description and action buttons (e.g. rejection reason field) */
  children?: ReactNode;
  /** Runs after the close transition ends; use to clear dialog copy without flashing while closing. */
  onDeleteModalExited?: () => void;
};

const TableDeleteConfirmDialog: FunctionComponent<Props> = ({
  dialogTitle,
  dialogDesc,
  isDeleteModalOpen,
  setIsDeleteModalOpen,
  onConfirm,
  deleteStatus,
  children,
  onDeleteModalExited
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={isDeleteModalOpen}
      maxWidth={"sm"}
      slotProps={
        onDeleteModalExited ? { transition: { onExited: onDeleteModalExited } } : undefined
      }
    >
      {dialogTitle && (
        <DialogTitle variant={"h5"} sx={{ mt: 1, mb: 1 }}>
          {dialogTitle}
        </DialogTitle>
      )}
      <DialogTitle variant={dialogTitle ? "subtitle1" : "h6"} sx={{ mt: 1, mb: 1 }}>
        {dialogDesc}
      </DialogTitle>
      <DialogContent sx={{ width: "100%" }}>
        {children}
        <DialogActions>
          <Grid2 container sx={{ justifyContent: "flex-end" }}>
            <Grid2 sx={{ paddingRight: "10px" }}>
              <Button
                color={"secondary"}
                data-testid={"delete_cancel_btn"}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                {t("Cancel")}
              </Button>
            </Grid2>
            <Grid2>
              <Button
                data-testid={"delete_confirm_btn"}
                loading={deleteStatus === "loading"}
                color={"primary"}
                variant={"contained"}
                onClick={onConfirm}
              >
                <span>{t("Confirm")}</span>
              </Button>
            </Grid2>
          </Grid2>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default TableDeleteConfirmDialog;
